import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import * as config from "../../config.js";
import { saveWorkflowGraph } from "../../services/workflows";
import { deleteTrigger, getTriggerTypes, patchTrigger, runTrigger } from "../../services/workflowTriggers";
import { buildTriggerPayload, normalizeTriggerTypes, summarizeTriggers } from "../../utils/workflowTriggers";
import TriggerCard from "./TriggerCard";
import TriggerEditor from "./TriggerEditor";
import TriggerRunHistory from "./TriggerRunHistory";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  color: var(--main-font-color);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;

  color: var(--main-font-color);`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  color: var(--main-font-color);`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  color: var(--main-font-color);`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;

  color: var(--main-font-color);`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background-color: var(--sidebar-active-item-bg);
  color: var(--main-font-color);
`;

const Summary = styled.div`
  font-size: 13px;
  color: #a0a6b1;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 13px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  background-color: var(--button-bg);
  color: var(--button-font-color);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 11px;
  font-size: 12.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  color: var(--main-font-color);`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 36px 20px;
  text-align: center;
  border: 1px dashed var(--main-border-color);
  border-radius: 10px;
  color: #a0a6b1;
  font-size: 13px;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;

  color: var(--main-font-color);`;

const TypeCard = styled.button`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  text-align: left;
  border: 1px solid var(--main-border-color);
  border-radius: 9px;
  background-color: var(--card-bg);
  color: var(--main-font-color);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--button-bg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const TypeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 7px;
  background-color: var(--sidebar-active-item-bg);
  font-size: 16px;

  color: var(--main-font-color);`;

const TypeText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;

  color: var(--main-font-color);`;

const TypeName = styled.span`
  font-size: 13px;
  font-weight: 700;

  color: var(--main-font-color);`;

const TypeDescription = styled.span`
  font-size: 11.5px;
  line-height: 1.4;
  color: #a0a6b1;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border: 1px solid var(--content-item-border-color);
  border-radius: 10px;
  background-color: var(--card-bg);

  color: var(--main-font-color);`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;

  color: var(--main-font-color);`;

/**
 * Instantiation ("triggers") for a standalone workflow: schedules, API endpoints, webhooks and
 * sub-workflow callers, plus the run history they produce.
 *
 * Triggers are edited locally and persisted by saving the workflow definition (the backend
 * reconciles the array); the enable toggle and "Run now" use their own endpoints.
 */
export const WorkflowTriggersTab = ({
  workflow = null,
  clientId = null,
  onSaved = () => {},
  style = {},
}) => {
  const workflowPublicId = workflow?.public_id ?? null;
  const clientPublicId = workflow?.client_public_id ?? null;

  const [catalog, setCatalog] = useState({ list: [], byType: {} });
  const [catalogError, setCatalogError] = useState(null);

  const [triggers, setTriggers] = useState(Array.isArray(workflow?.triggers) ? workflow.triggers : []);
  const [editor, setEditor] = useState(null); // { mode, typeMeta, draft }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [runsRefresh, setRunsRefresh] = useState(0);
  const [runTriggerId, setRunTriggerId] = useState("");
  const [actionError, setActionError] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);

  const endpointBase = config.REST_SERVER_URL;

  useEffect(() => {
    setTriggers(Array.isArray(workflow?.triggers) ? workflow.triggers : []);
  }, [workflow?.public_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;

    getTriggerTypes()
      .then((result) => {
        if (cancelled) return;
        if (result?.success === false) {
          setCatalogError(result.message || "Could not load the trigger types.");
          return;
        }
        setCatalog(normalizeTriggerTypes(result));
        setCatalogError(null);
      })
      .catch((error) => {
        if (!cancelled) setCatalogError(error?.message || "Could not load the trigger types.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the page's `updatedWorkflow.triggers` in step with the local list. The header's
  // "Save Changes" PUTs the whole definition, so a trigger removed here must not come back with
  // the next definition save.
  const syncDefinition = useCallback(
    (nextTriggers) => {
      if (!workflow) return;
      onSaved({ ...workflow, triggers: nextTriggers });
    },
    [workflow, onSaved]
  );

  /** PUT the whole definition with the desired trigger array; the backend reconciles it. */
  const persist = useCallback(
    async (nextTriggers) => {
      if (!workflow) return null;

      setSaving(true);
      setSaveError(null);
      setFieldErrors({});

      const payload = {
        ...workflow,
        triggers: nextTriggers.map((trigger) => buildTriggerPayload(trigger)),
      };

      const result = await saveWorkflowGraph(payload, clientId ?? workflow?.client_id ?? null);

      setSaving(false);

      if (!result?.success) {
        setSaveError(result?.message || "Could not save the triggers.");
        if (result?.field) setFieldErrors({ [result.field]: result.message });
        return null;
      }

      const saved = result.workflow ?? null;
      if (saved) {
        setTriggers(Array.isArray(saved.triggers) ? saved.triggers : nextTriggers);
        onSaved(saved);
      } else {
        setTriggers(nextTriggers);
      }

      return saved;
    },
    [workflow, clientId, onSaved]
  );

  const submitDraft = async (payload) => {
    const withoutDerived = buildTriggerPayload(payload);

    const next = payload.public_id
      ? triggers.map((trigger) => (trigger.public_id === payload.public_id ? { ...trigger, ...withoutDerived } : trigger))
      : [...triggers, withoutDerived];

    const saved = await persist(next);
    if (saved) {
      setEditor(null);
      setRunsRefresh((value) => value + 1);
    }
  };

  const toggle = async (trigger, enabled) => {
    const previous = triggers;
    setTriggers((current) => current.map((entry) => (entry.public_id === trigger.public_id ? { ...entry, enabled } : entry)));
    setBusyId(trigger.public_id);

    const result = await patchTrigger(trigger.public_id, { enabled });

    setBusyId(null);

    if (result?.success === false) {
      setTriggers(previous);
      setActionError(result.message || "Could not update the trigger.");
      return;
    }

    setTriggers((current) => {
      const next = result?.trigger
        ? current.map((entry) => (entry.public_id === trigger.public_id ? { ...entry, ...result.trigger } : entry))
        : current;

      syncDefinition(next);
      return next;
    });
  };

  const runNow = async (trigger) => {
    setBusyId(trigger.public_id);
    const result = await runTrigger(trigger.public_id);
    setBusyId(null);

    if (result?.success === false) {
      setActionError(result.message || "Could not start a run.");
      return;
    }

    setActionError(null);
    setRunsRefresh((value) => value + 1);
    setActionNotice(
      result?.run?.public_id
        ? `Run ${String(result.run.public_id).slice(0, 8)} queued — see Runs below.`
        : "Run queued — see Runs below."
    );
  };

  const remove = async (trigger) => {
    // The card's menu already asked for confirmation.
    setActionError(null);
    setActionNotice(null);

    const id = trigger.public_id ?? trigger.id ?? null;
    if (!id) {
      setActionError("This trigger has no id, so it cannot be deleted.");
      return;
    }

    setBusyId(trigger.public_id ?? trigger.id);

    let result;
    try {
      result = await deleteTrigger(id);
    } catch (failure) {
      result = { success: false, message: failure?.message || "Could not delete the trigger." };
    }

    setBusyId(null);

    // A 404 means it is already gone — treat that as success so the list catches up.
    const gone = result?.status === 404;

    if (result?.success === false && !gone) {
      setActionError(result.message || "Could not delete the trigger.");
      return;
    }

    const next = triggers.filter((entry) => (entry.public_id ?? entry.id) !== id);
    setTriggers(next);
    syncDefinition(next);
    setRunsRefresh((value) => value + 1);
  };

  const openCreate = () => setEditor({ mode: "create", typeMeta: null, draft: null });
  const openType = (typeMeta) => setEditor({ mode: "create", typeMeta, draft: null });
  const openEdit = (trigger) =>
    setEditor({ mode: "edit", typeMeta: catalog.byType[trigger.type] ?? { type: trigger.type, label: trigger.type }, draft: trigger });

  const count = triggers.length;
  const summary = useMemo(() => summarizeTriggers(triggers), [triggers]);
  const missingClientScope = !clientPublicId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px", height: "100%", overflow: "auto", ...style }}>
      <Wrapper>
        <Header>
          <HeaderText>
            <TitleRow>
              <Title>Instantiation</Title>
              <Badge>{count}</Badge>
            </TitleRow>
            <Summary>{summary}</Summary>
          </HeaderText>

          <span style={{ flex: 1 }} />

          <Button type="button" onClick={openCreate} disabled={Boolean(editor)}>
            <Icon icon="lucide:plus" /> Add instantiation method
          </Button>
        </Header>

        {catalogError ? <Empty>{catalogError}</Empty> : null}
        {actionError ? <Empty style={{ borderColor: "#e74c3c", color: "#e74c3c" }}>{actionError}</Empty> : null}
        {actionNotice ? <Empty style={{ borderColor: "#2ecc71", color: "#2ecc71" }}>{actionNotice}</Empty> : null}

        {editor ? (
          <Panel>
            {editor.mode === "create" && !editor.typeMeta ? (
              <>
                <PanelTitle>
                  <Icon icon="lucide:bolt" /> How should this workflow start?
                </PanelTitle>
                <TypeGrid>
                  {catalog.list.map((typeMeta) => {
                    const blocked = typeMeta.requires_api_key && missingClientScope;
                    return (
                      <TypeCard
                        key={typeMeta.type}
                        type="button"
                        disabled={blocked}
                        onClick={() => openType(typeMeta)}
                        title={blocked ? "This workflow has no client scope, so API/webhook triggers cannot be created." : undefined}
                      >
                        <TypeIcon><Icon icon={typeMeta.icon ?? "lucide:bolt"} /></TypeIcon>
                        <TypeText>
                          <TypeName>{typeMeta.label ?? typeMeta.type}</TypeName>
                          {typeMeta.description ? <TypeDescription>{typeMeta.description}</TypeDescription> : null}
                          {blocked ? <TypeDescription>Needs a client-scoped workflow.</TypeDescription> : null}
                        </TypeText>
                      </TypeCard>
                    );
                  })}
                </TypeGrid>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Ghost type="button" onClick={() => setEditor(null)}>Cancel</Ghost>
                </div>
              </>
            ) : (
              <>
                <PanelTitle>
                  <Icon icon={editor.typeMeta?.icon ?? "lucide:bolt"} />
                  {editor.mode === "edit" ? "Edit" : "New"} {editor.typeMeta?.label ?? "trigger"}
                </PanelTitle>

                <TriggerEditor
                  typeMeta={editor.typeMeta}
                  draft={editor.draft}
                  clientPublicId={clientPublicId}
                  workflowPublicId={workflowPublicId}
                  saving={saving}
                  error={saveError}
                  fieldErrors={fieldErrors}
                  onCancel={() => setEditor(null)}
                  onSubmit={submitDraft}
                />
              </>
            )}
          </Panel>
        ) : null}

        {count === 0 && !editor ? (
          <Empty>
            <Icon icon="lucide:calendar-clock" style={{ fontSize: "22px" }} />
            <div>This workflow only runs when someone clicks Run.</div>
            <div>Add a schedule, API endpoint, webhook, or sub-workflow caller.</div>
          </Empty>
        ) : (
          <List>
            {triggers.map((trigger) => (
              <TriggerCard
                key={trigger.public_id ?? trigger.name}
                trigger={trigger}
                typeMeta={catalog.byType[trigger.type] ?? { type: trigger.type, label: trigger.type }}
                endpointBase={endpointBase}
                busy={busyId === trigger.public_id}
                onToggle={toggle}
                onEdit={openEdit}
                onRunNow={runNow}
                onDelete={remove}
                onViewRuns={(target) => setRunTriggerId(target.public_id ?? "")}
              />
            ))}
          </List>
        )}

        <TriggerRunHistory
          workflowPublicId={workflowPublicId}
          triggers={triggers}
          refreshToken={runsRefresh}
          triggerId={runTriggerId}
          onTriggerIdChange={setRunTriggerId}
        />
      </Wrapper>
    </div>
  );
};

export default WorkflowTriggersTab;
